// ---------------------------------------------------------------------------
// Sales Intelligence, phase 4: brands, win / loss, forecast accuracy and pre-sales analysis.
// Built from the deals each person may see; closed deals are counted in the period they were closed.
// ---------------------------------------------------------------------------
import { z } from 'zod';

const iso = z.preprocess((v) => (v === '' ? undefined : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional());
export const analyticsFilterSchema = z.object({
  from: iso,
  to: iso,
  ownerId: z.preprocess((v) => (v === '' ? undefined : v), z.string().uuid().optional()),
});
export type AnalyticsFilter = z.infer<typeof analyticsFilterSchema>;

export interface Counted { key: string; count: number; value: number }

export interface BrandRow {
  key: string; deals: number; openValue: number; weighted: number; wonValue: number; lostValue: number; won: number; lost: number;
  winRate: number | null; avgDealSize: number | null; avgGp: number | null; expectedGp: number; approved: number; rejected: number;
  approvalRate: number | null; customers: { name: string; value: number }[]; consultants: { name: string; count: number }[];
}
export interface BrandAnalysis { rows: BrandRow[]; answers: { question: string; answer: string }[] }

export interface WinRateRow { key: string; label: string; won: number; lost: number; wonValue: number; lostValue: number; winRate: number | null }
export interface WinLossAnalysis {
  totals: { won: number; lost: number; wonValue: number; lostValue: number; winRate: number | null; valueWinRate: number | null; missingReasons: number };
  cycle: { wonDays: number | null; lostDays: number | null };
  margin: { won: number | null; lost: number | null };
  lossReasons: (Counted & { share: number })[];
  winReasons: Counted[];
  by: Record<'salesperson' | 'customer' | 'consultant' | 'contractor' | 'brand' | 'size' | 'system' | 'stage', WinRateRow[]>;
  insights: string[];
  missing: { id: string; name: string; isWon: boolean; closedOn: string; amount: number | null }[];
}

export type ForecastBias = 'accurate' | 'slightly_optimistic' | 'highly_optimistic' | 'slightly_conservative' | 'highly_conservative';
export const FORECAST_BIAS_LABEL: Record<ForecastBias, string> = {
  accurate: 'Accurate', slightly_optimistic: 'Slightly optimistic', highly_optimistic: 'Highly optimistic', slightly_conservative: 'Slightly conservative', highly_conservative: 'Highly conservative',
};
export interface ForecastAccuracyRow {
  userId: string; name: string; deals: number; acc30: number | null; acc90: number | null; accQuarter: number | null; accYtd: number | null;
  /** Signed average days late (+) or early (−) of the PO against the forecast. */
  dateErrorDays: number | null; poErrorAvgDays: number | null; poErrorMedianDays: number | null; within7: number | null; within14: number | null; within30: number | null;
  valueErrorPct: number | null; valueVariancePct: number | null; probabilityAccuracy: number | null; bias: ForecastBias | null;
}
export interface ForecastAccuracy {
  months: { month: string; forecast: number; actual: number; accuracy: number | null }[];
  rows: ForecastAccuracyRow[];
  team: ForecastAccuracyRow | null;
  note: string;
}

export interface PresalesRow {
  userId: string; name: string; assigned: number; active: number; pipeline: number; activeTenders: number; techReadiness: number | null;
  deadlines14: number; deadlinesWeek: number; overdueDeadlines: number; overdueTasks: number; pendingBoq: number; pendingDesign: number;
  pendingVendorQuotes: number; pendingSubmittals: number; pendingApprovals: number; quotationsReady: number;
  responseHours: number | null; completionDays: number | null; highRiskTenders: number; points: number; level: 'low' | 'normal' | 'high' | 'overloaded';
  upcoming: { id: string; name: string; deadline: string; readiness: number | null }[];
}
export interface PresalesAnalysis { rows: PresalesRow[] }

export const SIZE_BANDS = [
  { key: 'xs', label: 'Under 500K', max: 500_000 },
  { key: 's', label: '500K – 2M', max: 2_000_000 },
  { key: 'm', label: '2M – 5M', max: 5_000_000 },
  { key: 'l', label: '5M and above', max: Number.POSITIVE_INFINITY },
] as const;
export const sizeBand = (amount: number | null) => SIZE_BANDS.find((b) => (amount ?? 0) < b.max) ?? SIZE_BANDS[SIZE_BANDS.length - 1]!;
